import {
  Injectable,
  Inject,
  autobind,
  forwardRef,
} from '@kaokei/use-vue-service';

import { DaoService } from '../dao.service';

import { ExamService } from './exam.service';
import { TrtcService } from './trtc.service';
import { throttle } from 'lodash-es';
// import { notification } from 'ant-design-vue';

import {
  HeadPoseResults,
  PeopleResults,
  AudioResults,
  BodyPoseResults,
  InitState,
  FaceResults,
  ObjectResults,
} from '@/types/detect';
import { logger } from '@/utils';
import moment from 'moment';

// 异常类型枚举
enum AbnormalType {
  PEOPLE_WARNING = 'peopleWarning',
  FACE_NOT_IN_VIEW = 'faceNotInView',
  EARPHONE_WARNING = 'earphoneWarning',
  CELLPHONE_WARNING = 'cellphoneWarning',
}

// 异常记录接口
interface AbnormalRecord {
  type: AbnormalType;
  count: number;
  startTime: number;
  lastTime: number;
}

const warningMap: Record<AbnormalType, string> = {
  peopleWarning: '检测到人数超过1人',
  faceNotInView: '检测到人脸缺失',
  earphoneWarning: '检测到耳机',
  cellphoneWarning: '检测到手机',
};

// 创建警告函数的工厂函数
const createWarningFunction = (type: AbnormalType) => {
  return throttle(function () {
    logger.info(warningMap[type]);
    // 记录异常
    ActionDetectService.recordAbnormal(type);
  }, 1000 * 60);
};

const [peopleWarning, faceNotInView, earphoneWarning, cellphoneWarning] = [
  createWarningFunction(AbnormalType.PEOPLE_WARNING),
  createWarningFunction(AbnormalType.FACE_NOT_IN_VIEW),
  createWarningFunction(AbnormalType.EARPHONE_WARNING),
  createWarningFunction(AbnormalType.CELLPHONE_WARNING),
];
@Injectable()
export class ActionDetectService {
  @Inject(forwardRef(() => ExamService))
  public examService!: ExamService;
  @Inject(forwardRef(() => TrtcService))
  public trtcService!: TrtcService;

  public detector: SafeAny = null;
  public userExamId = 0;

  public inputVideo: HTMLVideoElement | null = null;
  public headPose: HeadPoseResults = {
    yaw: 0,
    pitch: 0,
    roll: 0,
  };
  public nodScore = 0;
  public raiseScore = 0;
  public yawScore = 0;

  public bodyPose: BodyPoseResults = {
    raiseHand: 0,
    stand: 0,
  };

  public people: PeopleResults = {
    count: 0,
    scores: [],
  };

  public faceInView = 100;

  public audioResults: AudioResults = {
    speech: 0,
  };

  public faceSimilarity = 0;
  public faceNotFound = false;
  public earphone = {
    score: 0,
    name: '',
  };

  public cellphone = {
    score: 0,
  };

  // 异常记录数组
  private static abnormalRecords: AbnormalRecord[] = [];

  // 上次发送记录的时间
  private static lastSendTime = 0;

  // 定时发送任务ID
  private sendTaskId: number | null = null;

  // 默认发送间隔（毫秒）
  private static readonly DEFAULT_SEND_INTERVAL = 60000; // 1分钟

  public get userIdVal() {
    const sessionUserId = window.sessionStorage.getItem('pc_contest_user_id');
    return (
      this.examService?.userInfo4Exam?.userId ||
      this.examService?.userService?.user?.userId ||
      Number(sessionUserId) ||
      0
    );
  }

  // 记录异常
  public static recordAbnormal(type: AbnormalType): void {
    const now = Date.now();
    // 获取当前时间段信息（北京时区）

    const existingRecord = this.abnormalRecords.find(
      record => record.type === type
    );

    if (existingRecord) {
      // 更新已存在的记录
      existingRecord.count += 1;
      existingRecord.lastTime = now;
    } else {
      // 创建新记录
      this.abnormalRecords.push({
        type,
        count: 1,
        startTime: now,
        lastTime: now,
      });
    }
  }

  constructor(public daoService: DaoService) {}

  @autobind
  public EventTrack(data: Record<string, any>, type = 'element_show') {
    window.hetaoTrack(type, {
      pageName: '赛考页面',
      userId: this.userIdVal,
      examId: this.examService.userExamId ?? -1,
      ...data,
    });
  }

  @autobind
  public init(inputVideo: HTMLVideoElement) {
    if (process.env.NODE_ENV === 'development') {
      // 开发环境别开启了
      return;
    }
    if (!this.examService.enableDetect) {
      this.EventTrack({
        elementName: '不启用AI检测',
      });
      return;
    }
    if (!this.examService.licenceUrl) {
      this.EventTrack({
        elementName: '未配置licenceUrl',
      });
      return;
    }
    this.detector = new (window as any).ActionDetectionSDK({
      fps: 1,
      headPose: undefined,
      bodyPose: undefined,
      people: {
        number: true,
      },
      audio: undefined,
      face: {
        fullFaceInView: true,
        faceCompare: true,
      },
      object: {
        earphone: true,
        cellphone: true,
      },
      license: {
        url: this.examService.licenceUrl,
      },
    });
    this.inputVideo = inputVideo;
    this.initState();
    this.initEvent();
  }

  @autobind
  public initData(data: { userExamId: number }) {
    this.userExamId = data.userExamId;
  }

  @autobind
  public initState() {
    const detector = this.detector;
    detector?.on('initState', async ({ code, message }: InitState) => {
      if (code === 0) {
        if (this.inputVideo) {
          detector.startDetection(this.inputVideo);
        }
        // 初始化时启动定时发送任务
        ActionDetectService.lastSendTime = Date.now();
        this.startSendTask();
      } else {
        logger.info(message);
        logger.info(code);
      }
    });
  }

  @autobind
  public initEvent() {
    this.handleFaceDetectionResults();
    this.handlePeopleDetectionResults();
    this.handleObjectDetectionResults();
  }

  @autobind
  public handleFaceDetectionResults() {
    const detector = this.detector;
    detector?.on('headPoseResults', (results: HeadPoseResults) => {
      this.headPose = results;
    });
    detector?.on(
      'faceResults',
      ({ fullFaceInView, faceSimilarity }: FaceResults) => {
        if (fullFaceInView !== undefined) {
          this.faceInView = fullFaceInView;
          if (fullFaceInView < 100) {
            faceNotInView();
          }
        }

        if (faceSimilarity !== undefined) {
          const s = Math.ceil(faceSimilarity * 10000) / 100;
          this.faceSimilarity = s;
        }
      }
    );
  }

  @autobind
  public handlePeopleDetectionResults() {
    const detector = this.detector;
    detector?.on('peopleResults', (results: PeopleResults) => {
      this.people = results;

      if (results.count > 1) {
        peopleWarning();
      }
    });
  }

  @autobind
  public handleObjectDetectionResults() {
    const detector = this.detector;
    detector?.on('objectResults', (results: ObjectResults) => {
      const { earphone, cellPhone } = results;

      if (earphone !== undefined) {
        if (earphone?.length) {
          const e = earphone[0];
          this.earphone = e;
          if (e.score > 40) {
            earphoneWarning();
          }
        } else {
          this.earphone = { score: 0, name: '' };
        }
      }

      if (cellPhone !== undefined) {
        if (cellPhone?.length) {
          const c = cellPhone[0];
          this.cellphone = { score: c.score };
          if (c.score > 40) {
            cellphoneWarning();
          }
        } else {
          this.cellphone = { score: 0 };
        }
      }
    });
  }

  // 启动定期发送任务
  @autobind
  public startSendTask(
    interval: number = ActionDetectService.DEFAULT_SEND_INTERVAL
  ): void {
    // 先停止之前的任务
    this.stopSendTask();

    // 启动新的定时任务
    this.sendTaskId = window.setInterval(() => {
      this.sendAbnormalRecords();
    }, interval);
  }

  // 停止定期发送任务
  @autobind
  public stopSendTask(): void {
    if (this.sendTaskId !== null) {
      window.clearInterval(this.sendTaskId);
      this.sendTaskId = null;
    }
  }

  // 发送异常记录到服务器
  @autobind
  private sendAbnormalRecords(): void {
    const records = ActionDetectService.abnormalRecords;
    const currentTime = Date.now();

    if (records.length > 0) {
      // 构建JSON格式数据
      const recordData: Record<string, any> = {
        startTime: moment(ActionDetectService.lastSendTime)
          .utcOffset(8)
          .valueOf(),
        endTime: moment(currentTime).utcOffset(8).valueOf(),
        userId: this.userIdVal,
        userExamId: this.userExamId,
      };

      // 统计各类型异常的次数
      const eventData: Record<string, number> = {};
      records.forEach(record => {
        if (eventData[record.type]) {
          eventData[record.type] += record.count;
        } else {
          eventData[record.type] = record.count;
        }
      });
      recordData.eventData = JSON.stringify(eventData);

      // 使用 daoService 发送数据到服务器
      this.daoService.postActionRecords(recordData).then(() => {
        // 发送成功后清除记录并更新发送时间
        ActionDetectService.lastSendTime = currentTime;
        ActionDetectService.clearAbnormalRecords();
      });
    }
  }
  // 清除异常记录
  public static clearAbnormalRecords(): void {
    this.abnormalRecords = [];
  }

  destroy() {
    // 停止定时发送任务
    this.stopSendTask();
    this.detector?.destroy();
  }
}
